/* @jsx h */
import { cn, useLocalization } from "@lemmonhq/uikit";
import {
  TableStatus,
  Reservation,
  ACTIVE_TABLE_STATUSES,
} from "@lemmonhq/types";
import { defineComponent, h, computed, inject } from "vue";
import { timeWindowsOverlap } from "@lemmonhq/blogic";
import {
  reservationsForTable,
  isConfirmedStatus,
  getArrivalState,
  formatTimeHHmm,
  formatTimeRangeHHmm,
} from "../../../../../utils/reservationStatus";
import { TableVisualProps } from "./types";
import { TableAmountLines } from "@app/vue/components/TableCredit";

export const AssignedTableCell = defineComponent<
  TableVisualProps & {
    tableStatus: TableStatus;
    nowMs: number;
  }
>({
  name: "AssignedTableCell",
  props: [
    "planConfig",
    "tableStatus",
    "onFloorPlanCellChange",
    "tables",
    "onTableOpen",
    "dropModeState",
    "reservations",
    "isLiveDay",
    "nowMs",
  ],
  setup(props) {
    const { t } = useLocalization();
    // Restaurant timezone (provided by TablesPage) so ribbon/booking times
    // render in the restaurant's tz, not the manager's device tz.
    const timezone = inject<{ value: string }>("timezone", { value: "UTC" });
    const table = props.planConfig;
    const isRound = computed(() => table.shape === "round");
    // Live occupancy (busy state, current party) is only meaningful for today.
    // For future dates the floor reflects that day's reservations only.
    const isLiveDay = computed(() => props.isLiveDay !== false);

    const tableDimensions = computed(() => ({
      width: table.colEnd - table.colStart + 1,
      height: table.rowEnd - table.rowStart + 1,
    }));

    const chairCounts = computed(() => {
      const { width, height } = tableDimensions.value;
      const area = width * height;
      const customSeats = table.seats;

      // Default seats: 4 per cell for rectangle, 3 per cell for round
      const defaultSeats = isRound.value
        ? Math.max(3, area * 3)
        : Math.max(4, area * 4);
      const totalSeats = customSeats || defaultSeats;

      if (isRound.value) {
        // Round/oval: distribute chairs radially around perimeter
        return { total: totalSeats, top: 0, bottom: 0, left: 0, right: 0 };
      }

      // Rectangle: distribute across edges proportionally to edge length
      // Cap per edge: max 2 chairs per cell of edge length
      const maxPerEdge = (len: number) => Math.max(1, len * 2);
      const perimeter = 2 * (width + height);
      let topCount = Math.min(
        maxPerEdge(width),
        Math.max(1, Math.round(totalSeats * (width / perimeter))),
      );
      let bottomCount = Math.min(
        maxPerEdge(width),
        Math.max(1, Math.round(totalSeats * (width / perimeter))),
      );
      let leftCount = Math.min(
        maxPerEdge(height),
        Math.max(1, Math.round(totalSeats * (height / perimeter))),
      );
      let rightCount = Math.max(
        0,
        totalSeats - topCount - bottomCount - leftCount,
      );
      rightCount = Math.min(maxPerEdge(height), rightCount);

      return {
        total: 0,
        top: topCount,
        bottom: bottomCount,
        left: leftCount,
        right: rightCount,
      };
    });

    const getTimeSinceFirstOrder = () => {
      if (!props.tableStatus.orders.length) return null;
      const firstOrderTime = Math.min(
        ...props.tableStatus.orders.map((o) =>
          new Date(o.created_at).getTime(),
        ),
      );
      const diffInMinutes = Math.floor((props.nowMs - firstOrderTime) / 60000);
      const hours = Math.floor(diffInMinutes / 60);
      const minutes = String(diffInMinutes % 60).padStart(2, "0");
      return `${hours}:${minutes}h`;
    };

    // This table's bookings (confirmed/changed + seated set), time-sorted — used
    // for both the ribbons and the conflict check. Prefer the parent-level
    // per-table bucket (O(1) lookup; built once for the whole floor); fall back
    // to scanning the full list if the bucket wasn't provided.
    const tableReservations = computed(() => {
      const tableId = props.planConfig.tableId;
      if (!tableId) return [];
      if (props.reservationsByTable) {
        return props.reservationsByTable.get(Number(tableId)) ?? [];
      }
      if (!props.reservations) return [];
      return reservationsForTable(
        props.reservations,
        Number(tableId),
        ACTIVE_TABLE_STATUSES,
      );
    });

    // Confirmed/changed bookings not yet seated — the floor ribbons.
    const upcomingReservations = computed(() =>
      tableReservations.value.filter((r) => isConfirmedStatus(r.status)),
    );

    const nextReservation = computed(() => upcomingReservations.value[0]);

    // While seating/assigning, is this table already taken for the active
    // reservation's time window? (another confirmed/seated booking overlaps).
    // This is independent of live "busy now" — it answers "is it free for THAT
    // time", which is what matters when planning, on today or any future date.
    const conflictsWithActive = computed(() => {
      // The active target may be a reservation (.time) or a pending request
      // (.requested_time) being assigned tables on the floor.
      const ds = props.dropModeState?.value;
      const active = ds?.reservation || ds?.request;
      if (!active) return false;
      const activeStartIso =
        (active as any).time ?? (active as any).requested_time;
      const aStart = new Date(activeStartIso).getTime();
      const aEnd = aStart + active.duration_mins * 60000;
      // tableReservations is already filtered to this table + table-holding
      // statuses (incl. arrived / partially-seated), so we only need the
      // overlap check here.
      return tableReservations.value.some((r) => {
        if (r.id === active.id) return false;
        const rStart = new Date(r.time).getTime();
        const rEnd = rStart + r.duration_mins * 60000;
        return timeWindowsOverlap(
          { startMs: aStart, endMs: aEnd },
          { startMs: rStart, endMs: rEnd },
        );
      });
    });

    const formatReservationTime = (reservation: Reservation) =>
      formatTimeRangeHHmm(
        reservation.time,
        reservation.duration_mins,
        timezone.value,
      );

    const getDropModeColor = () => {
      const tableId = Number(props.planConfig.tableId);
      const isSelected =
        !!tableId &&
        props.dropModeState?.value.selectedTableIds?.some(
          (id) => Number(id) === tableId,
        );
      // "Editing" = a specific reservation OR a pending request is being assigned.
      const editingReservation = !!(
        props.dropModeState?.value.reservation ||
        props.dropModeState?.value.request
      );
      const isBusy = isLiveDay.value && props.tableStatus.table.is_busy;
      const hasUpcoming = upcomingReservations.value.length > 0;
      // Arriving soon or already late — needs seating now (same window as the
      // ribbon, so the ring and the ribbon escalate together).
      const hasImminent =
        isLiveDay.value &&
        upcomingReservations.value.some(
          (r) => getArrivalState(r.time, props.nowMs) !== "upcoming",
        );

      // Colors are applied via inline style (background + content), so the drop
      // state carries a `style` string; only the ring stays a Tailwind class.
      const amber = (selected: boolean, imminent = false) => ({
        // Selected (being assigned) or imminent-arrival tables get a ring so
        // they stand out among other reserved (amber) tables.
        ring:
          selected || imminent
            ? "ring-2 ring-[var(--floor-plan-table-reserved-ring)] ring-offset-2 ring-offset-base-100"
            : "",
        style:
          "background: var(--floor-plan-table-reserved-background); color: var(--floor-plan-table-reserved-content);" +
          (imminent
            ? " box-shadow: 0 0 18px var(--floor-plan-table-reserved-glow); animation: pulse 1.8s cubic-bezier(0.4, 0, 0.6, 1) infinite;"
            : ""),
      });
      const red = {
        ring: "",
        style:
          "background: var(--floor-plan-table-unavailable-background); color: var(--floor-plan-table-unavailable-content);",
      };
      const gray = {
        ring: "",
        style:
          "background: var(--floor-plan-table-free-background); color: var(--floor-plan-table-free-content);",
      };

      // Same colour scheme as browse mode (reserved = amber, busy = red,
      // free = gray). While editing a reservation, "reserved" follows the live
      // selection — so deselecting a table turns it gray, not amber.
      if (editingReservation) {
        if (isSelected) return amber(true);
        // Unavailable for this reservation's time slot — busy now (today) or
        // already booked by an overlapping reservation → red, on any date.
        if (isBusy || conflictsWithActive.value) return red;
        return gray;
      }
      if (isBusy) return red;
      if (hasUpcoming) return amber(false, hasImminent);
      return gray;
    };

    // All chairs same color, same size, everywhere
    const CHAIR = {
      bg: "var(--floor-plan-chair-background)",
      border: "1px solid var(--floor-plan-chair-border)",
      shadow: "var(--floor-plan-chair-shadow)",
      // Backrest shape: wide and shallow
      w: 28, // px — width of backrest (along table edge)
      d: 11, // px — depth of backrest (sticking out from edge)
      radius: {
        // curved on outside, flat on table side
        top: "4px 4px 1px 1px",
        bottom: "1px 1px 4px 4px",
        left: "4px 1px 1px 4px",
        right: "1px 4px 4px 1px",
      } as Record<string, string>,
    };
    // Table fills 100% of cell. Chairs overlap the edge from outside.
    const TABLE_INSET = 0;

    const renderRoundChairs = () => {
      const total = chairCounts.value.total;
      // Rectangular backrests around the circle, same style as rect tables
      return Array.from({ length: total }, (_, i) => {
        const angle = (360 / total) * i - 90;
        const rad = (angle * Math.PI) / 180;
        // Place at 50% — centered on the circle edge
        const x = 50 + Math.cos(rad) * 50;
        const y = 50 + Math.sin(rad) * 50;
        return (
          <div
            key={i}
            class="absolute"
            style={{
              zIndex: 1,
              width: `${CHAIR.w}px`,
              height: `${CHAIR.d}px`,
              left: `${x}%`,
              top: `${y}%`,
              transform: `translate(-50%, -50%) rotate(${angle + 90}deg)`,
              borderRadius: "4px 4px 1px 1px",
              background: CHAIR.bg,
              border: CHAIR.border,
              boxShadow: CHAIR.shadow,
            }}
          />
        );
      });
    };

    const renderRectChairs = () => {
      const counts = chairCounts.value;

      const edge = (
        edgeName: "top" | "bottom" | "left" | "right",
        count: number,
      ) => {
        const isV = edgeName === "left" || edgeName === "right";
        // Chairs centered on the table edge (padding boundary)
        const pos: Record<string, Record<string, string>> = {
          top: {
            top: "0",
            left: "50%",
            transform: "translateX(-50%) translateY(-50%)",
          },
          bottom: {
            bottom: "0",
            left: "50%",
            transform: "translateX(-50%) translateY(50%)",
          },
          left: {
            left: "0",
            top: "50%",
            transform: "translateY(-50%) translateX(-50%)",
          },
          right: {
            right: "0",
            top: "50%",
            transform: "translateY(-50%) translateX(50%)",
          },
        };

        return (
          <div
            class={cn(
              "absolute flex justify-evenly items-center",
              isV ? "flex-col" : "flex-row",
            )}
            style={{
              zIndex: 1,
              ...(isV
                ? { height: `calc(100% - ${CHAIR.d + 4}px)` }
                : { width: `calc(100% - ${CHAIR.w + 4}px)` }),
              ...pos[edgeName],
            }}
          >
            {Array.from({ length: count }, (_, i) => (
              <div
                key={i}
                style={{
                  width: `${isV ? CHAIR.d : CHAIR.w}px`,
                  height: `${isV ? CHAIR.w : CHAIR.d}px`,
                  flexShrink: "0",
                  borderRadius: CHAIR.radius[edgeName],
                  background: CHAIR.bg,
                  border: CHAIR.border,
                  boxShadow: CHAIR.shadow,
                }}
              />
            ))}
          </div>
        );
      };

      return [
        edge("top", counts.top),
        edge("bottom", counts.bottom),
        edge("left", counts.left),
        edge("right", counts.right),
      ];
    };

    return () => {
      const isBusy = isLiveDay.value && props.tableStatus.table.is_busy;
      const isDropMode = props.dropModeState?.value.active;
      const dropColors = isDropMode ? getDropModeColor() : null;
      const round = isRound.value;
      const time = getTimeSinceFirstOrder();


      // All upcoming reservations for this table today
      const reservationsToShow = upcomingReservations.value;

      return (
        <div class="relative w-full h-full select-none">
          {/* Rect chairs — before table surface so table covers inner half */}
          {!round && renderRectChairs()}

          {/* Table surface — fills remaining space after padding */}
          <div
            class={cn(
              "w-full h-full flex flex-col justify-center items-center gap-0.5 relative floor-plan-table overflow-hidden",
              round ? "rounded-full" : "rounded-lg",
              isDropMode ? dropColors?.ring : "",
            )}
            style={`z-index: 2; transition: background 0.8s ease, box-shadow 0.2s ease, transform 0.2s ease; cursor: pointer; ${
              isDropMode
                ? (dropColors?.style ?? "")
                : `background: var(--floor-plan-table-${isBusy ? "busy" : "free"}-background); box-shadow: var(--floor-plan-table-${isBusy ? "busy" : "free"}-shadow); color: var(--floor-plan-table-${isBusy ? "busy" : "free"}-content);`
            }`}
          >
            {/* Content — table name + party */}
            <div class="overflow-hidden w-full px-1">
              <div
                class="truncate flex justify-center items-center font-semibold"
                style="font-size: clamp(0.65rem, 1.8cqi, 0.95rem);"
              >
                {props.tableStatus.table.name}
              </div>
              {isLiveDay.value && props.tableStatus.party?.name ? (
                <div
                  class="truncate text-center opacity-70"
                  style="font-size: clamp(0.65rem, 2.1cqi, 1.1rem);"
                >
                  {props.tableStatus.party.name}
                </div>
              ) : nextReservation.value?.party_name ? (
                // Reserved (not seated) — name the upcoming guest on the table.
                <div
                  class="truncate text-center opacity-80 font-medium"
                  style="font-size: clamp(0.45rem, 1.2cqi, 0.7rem);"
                >
                  {nextReservation.value.party_name}
                </div>
              ) : null}
            </div>

            {/* Timer + revenue — only when busy, only in default view */}
            {isBusy && !isDropMode ? (
              <div
                class="flex items-center justify-center gap-1 opacity-80"
                style="font-size: clamp(0.45rem, 1.2cqi, 0.7rem);"
              >
                {time && (
                  <span class="flex items-center gap-0.5">
                    <span
                      class="material-symbols-outlined"
                      style="font-size: inherit;"
                    >
                      history
                    </span>
                    {time}
                  </span>
                )}
                <TableAmountLines tableStatus={props.tableStatus} compact={true} />
              </div>
            ) : null}
          </div>

          {/* Round chairs — after table surface so they show in circle corners */}
          {round && renderRoundChairs()}

          {/* Reservation ribbons — top-right corner, stacked. Shown in both
              orders and reservation views. A booking turns orange + blinks
              ("Arriving soon") from 15 min before its time, then red + blinks
              ("Late") once 30 min past, until it's seated (seated bookings drop
              out of upcomingReservations). pointer-events-none so taps still
              reach the table. */}
          {reservationsToShow.length > 0 ? (
            <div class="absolute top-0.5 right-0.5 z-10 flex flex-col items-end gap-0.5 pointer-events-none">
              {reservationsToShow.slice(0, 3).map((res) => {
                // One shared arrival window (15 min soon / 30 min late) so the
                // ribbon agrees with the sidebar list for the same booking.
                const arrival = getArrivalState(res.time, props.nowMs);
                const isLate = arrival === "late";
                const isSoon = arrival === "soon";
                const bg = isLate
                  ? "var(--floor-plan-reservation-late-background)"
                  : "var(--floor-plan-reservation-background)";
                const label = isLate
                  ? t("reservations.floor.late")
                  : isSoon
                    ? t("reservations.floor.arriving_soon")
                    : "";
                return (
                  <div
                    key={res.id}
                    class={cn(
                      "rounded-md font-bold shadow-md",
                      isLate || isSoon ? "res-ribbon-blink" : "",
                    )}
                    style={`font-size: clamp(8px, 1.8cqi, 12px); line-height: 1.4; padding: 1px 6px; background: ${bg}; color: var(--floor-plan-reservation-content); white-space: nowrap;`}
                    title={`${res.party_name} — ${formatReservationTime(res)}`}
                  >
                    {formatTimeHHmm(res.time, timezone.value)}
                    {label ? (
                      <span class="ml-1 font-semibold">{label}</span>
                    ) : null}
                  </div>
                );
              })}
              {reservationsToShow.length > 3 && (
                <div
                  class="rounded-md font-bold shadow-md"
                  style="font-size: clamp(7px, 1.4cqi, 10px); line-height: 1.4; padding: 0 4px; background: var(--floor-plan-reservation-overflow-background); color: var(--floor-plan-reservation-content);"
                >
                  +{reservationsToShow.length - 3}
                </div>
              )}
            </div>
          ) : null}
        </div>
      );
    };
  },
});
